import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';

// Multi-select dropdown shaped like the .docs-filter-control pill from the
// filter-bar spec (FILTER_AND_DATE_RULES.md §2): idle border when empty,
// blue accent + count badge when ≥1 selected, commits on every click (no
// Apply button), closes on outside-click / Escape / Done.
//
// `values` is a string[] of currently selected options. `options` is a
// string[] of all available options. `onChange` receives the new array.
//
// Optional typeahead (set `searchable`): renders a search input at the top
// of the panel; once the query reaches `minSearchChars` (default 2) the
// options list filters in-memory. Matching is on the *displayed* string
// (renderOption result if provided) so ID-keyed lists like suppliers can
// match by human-readable name. All work is `useMemo`'d — filter runs once
// per query change over what is in scope a small-to-medium list (≤ a few
// hundred items), which stays well under a millisecond.
//
// `inline` mode (mobile FilterSheet): instead of a pill + absolute dropdown,
// renders an expandable full-width section — header row toggles a checkbox
// list with 44px touch rows. Selection semantics are identical (commits on
// every click).
export default function MultiSelectFilter({
  label,
  values,
  options,
  onChange,
  renderOption,
  // Optional (opt) => node|null: an explanation icon (e.g. <StatInfo>) beside
  // the option. Rendered NEXT TO the <label>, never inside it — a tap on the
  // icon, its panel or its backdrop would otherwise also tick the checkbox.
  renderOptionInfo,
  searchable = false,
  minSearchChars = 2,
  // Text shown in the trigger pill when no value is selected. Default 'הכל'
  // suits most filters (selecting nothing = "all"); set to '' to suppress
  // for filters that always expect at least one logical selection.
  emptyLabel = 'הכל',
  inline = false,
  // Opt-in: a "בחר הכל" button in the panel footer, calling this instead of
  // onChange so the page can treat "everything" as its own choice (it is not
  // the same as ticking every option one by one). Shown only while not
  // everything is selected and no typeahead query is typed — with a query the
  // visible matches would look like what "all" means. Omitted = no button, so
  // existing filters are unchanged.
  onSelectAll,
  // Optional text for the trigger instead of "N נבחרו" — e.g. "הכל חוץ מ-1"
  // when a page reads a large selection as "all but these".
  summaryText,
}) {
  const display = (opt) => (renderOption ? renderOption(opt) : opt);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const isActive  = values.length > 0;
  const summary   = summaryText || (isActive ? `${values.length} נבחרו` : emptyLabel);
  const canSelectAll = typeof onSelectAll === 'function' && options.length > 0
    && values.length < options.length && !query.trim();

  useEffect(() => {
    // Escape-to-close is a dropdown affordance; in inline (sheet) mode the
    // hosting FilterSheet owns Escape.
    if (!open || inline) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, inline]);

  // Reset query each time the panel closes so the next open starts clean.
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // Filtering kicks in only once the query is meaningfully long.
  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim();
    if (q.length < minSearchChars) return options;
    return options.filter(opt => {
      const text = String(display(opt) ?? '');
      return text.includes(q);
    });
    // `display` references `renderOption` from props but is stable per render;
    // depending on options + query + renderOption is sufficient.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, query, renderOption, searchable, minSearchChars]);

  const toggleValue = (v) => {
    const next = values.includes(v)
      ? values.filter(x => x !== v)
      : [...values, v];
    onChange(next);
  };

  // Search input + checkbox list — identical in both modes; only the chrome
  // around it (absolute dropdown vs inline section) differs.
  const optionCls = inline ? 'msf-inline-option' : 'multi-select-option';
  const panelBody = (
    <>
      {searchable && (
        <input
          ref={inputRef}
          className="multi-select-search"
          placeholder={`חיפוש (${minSearchChars}+ תווים)`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <div className={inline ? 'msf-inline-list' : 'multi-select-list'}>
        {filteredOptions.length === 0 ? (
          <div className="multi-select-empty">
            {options.length === 0 ? 'אין אפשרויות' : 'לא נמצאו תוצאות'}
          </div>
        ) : (
          filteredOptions.map(opt => {
            const checked = values.includes(opt);
            const info = renderOptionInfo ? renderOptionInfo(opt) : null;
            const row = (
              <label key={info ? undefined : opt} className={`${optionCls}${checked ? ' is-checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleValue(opt)}
                />
                <span>{display(opt)}</span>
              </label>
            );
            return info ? <div key={opt} className="msf-option-row">{row}{info}</div> : row;
          })
        )}
      </div>
    </>
  );

  if (inline) {
    return (
      <div className="msf-inline">
        <button
          type="button"
          className={`msf-inline-header${isActive ? ' is-active' : ''}`}
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
        >
          <span className="msf-inline-label">{label}</span>
          <span className="msf-inline-summary">{summary}</span>
          {isActive && <span className="multi-select-count">{values.length}</span>}
          <Icon name="chevron" size={12} className={`docs-filter-caret${open ? ' is-open' : ''}`} />
        </button>
        {open && (
          <div className="msf-inline-panel" role="group" aria-label={label}>
            {panelBody}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="multi-select">
      <button
        type="button"
        className={`docs-filter-control${isActive ? ' is-active' : ''}`}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && searchable) setTimeout(() => inputRef.current?.focus(), 0);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        style={{ cursor: 'pointer' }}
      >
        <span className="docs-filter-microlabel">{label}</span>
        <span className="docs-filter-select" style={{ pointerEvents: 'none' }}>
          {summary}
        </span>
        {isActive && (
          <span className="multi-select-count">{values.length}</span>
        )}
        <Icon name="chevron" size={12} className="docs-filter-caret" />
      </button>

      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          {/* Plain group, not role="listbox" — children are real checkboxes,
              so AT software reads native semantics correctly without us
              having to mirror the listbox/option contract. */}
          <div className="multi-select-panel" role="group" aria-label={label}>
            {panelBody}
            <div className="multi-select-footer">
              {canSelectAll && (
                <button type="button" className="multi-select-clear" onClick={onSelectAll}>
                  בחר הכל
                </button>
              )}
              {isActive && (
                <button type="button" className="multi-select-clear" onClick={() => onChange([])}>
                  נקה
                </button>
              )}
              <button type="button" className="multi-select-done" onClick={() => setOpen(false)}>
                סיום
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
